import React, { useState, useEffect } from 'react';
import { CampCategory } from '../../types/database';
import { getCategoriesGroupedByType } from '../../lib/supabase';
import { trackEvent } from '../../lib/posthog';

interface FocusAreaChipsProps {
  selectedCategories: string[];
  onCategoryChange: (categoryIds: string[]) => void;
  className?: string;
}

export default function FocusAreaChips({ 
  selectedCategories, 
  onCategoryChange, 
  className = '' 
}: FocusAreaChipsProps) {
  const [focusAreaCategories, setFocusAreaCategories] = useState<CampCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadFocusAreaCategories() {
      const categories = await getCategoriesGroupedByType();
      if (categories && categories.focus_area) {
        setFocusAreaCategories(categories.focus_area);
      }
      setLoading(false);
    }

    loadFocusAreaCategories();
  }, []);

  const handleCategoryToggle = (categoryId: string) => {
    const newSelected = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    // Track category selection in PostHog
    const category = focusAreaCategories.find(cat => cat.id === categoryId);
    const isAdding = !selectedCategories.includes(categoryId);
    
    trackEvent('category_toggled', {
      category_id: categoryId,
      category_name: category?.name || 'Unknown',
      action: isAdding ? 'added' : 'removed',
      total_selected: newSelected.length,
      previous_selected: selectedCategories.length
    });
    
    onCategoryChange(newSelected);
  };

  const handleSelectAll = () => {
    const allFocusAreaIds = focusAreaCategories.map(cat => cat.id);
    const hasAllSelected = allFocusAreaIds.every(id => selectedCategories.includes(id));
    
    if (hasAllSelected) {
      // Remove all focus area categories
      const newSelected = selectedCategories.filter(id => !allFocusAreaIds.includes(id));
      onCategoryChange(newSelected);
    } else {
      // Add all focus area categories
      const newSelected = [...selectedCategories];
      allFocusAreaIds.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      onCategoryChange(newSelected);
    }
  };

  if (loading) {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-8 bg-gray-200 rounded-full animate-pulse w-20"></div>
        ))}
      </div>
    );
  }

  const selectedFocusAreaCount = focusAreaCategories.filter(cat => 
    selectedCategories.includes(cat.id)
  ).length;

  return (
    <>
      {focusAreaCategories.map((category) => (
        <button
          key={category.id}
          onClick={() => handleCategoryToggle(category.id)}
          className={`category-chip inline-flex items-center gap-1.5 ${
            selectedCategories.includes(category.id)
              ? 'category-chip-selected'
              : 'category-chip-default'
          }`}
        >
          {category.icon_path && (
            <img 
              src={category.icon_path} 
              alt="" 
              className="w-4 h-4 flex-shrink-0 category-chip-icon"
              aria-hidden="true"
            />
          )}
          {category.name}
        </button>
      ))}
    </>
  );
} 